import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../config/axios';
import { connectSocket, disconnectSocket } from '../config/socket';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import {
  InboxIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  HandRaisedIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const TeamInbox = () => {
  const { user } = useAuth();

  // State
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await api.get(`/inbox/conversations?${params}`);
      setConversations(response.data.conversations || []);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  }, [statusFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/inbox/stats');
      setStats(response.data.stats);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  const selectConversation = useCallback(async (conv) => {
    try {
      setSelectedConversation(conv);
      const response = await api.get(`/inbox/conversations/${conv.id}`);
      setMessages(response.data.messages || []);
      setTimeout(scrollToBottom, 100);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      toast.error('Failed to load conversation');
    }
  }, []);

  const handleTakeover = async () => {
    if (!selectedConversation) return;
    try {
      await api.post(`/inbox/conversations/${selectedConversation.id}/takeover`);
      toast.success('Conversation taken over');
      fetchConversations();
      selectConversation(selectedConversation);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to take over');
    }
  };

  const handleRelease = async () => {
    if (!selectedConversation) return;
    try {
      await api.post(`/inbox/conversations/${selectedConversation.id}/release`);
      toast.success('Conversation released to bot');
      fetchConversations();
      selectConversation(selectedConversation);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to release');
    }
  };

  const handleResolve = async () => {
    if (!selectedConversation) return;
    try {
      await api.post(`/inbox/conversations/${selectedConversation.id}/resolve`);
      toast.success('Conversation resolved');
      fetchConversations();
      setSelectedConversation(null);
      setMessages([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resolve');
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedConversation) return;

    try {
      setSending(true);
      const response = await api.post(`/inbox/conversations/${selectedConversation.id}/reply`, {
        message: replyText
      });

      setMessages(prev => [...prev, response.data.data]);
      setReplyText('');
      setTimeout(scrollToBottom, 100);
      toast.success('Reply sent');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  // Socket.IO real-time updates
  useEffect(() => {
    const socket = connectSocket();
    if (!socket) return;

    socket.on('new-message', ({ conversationId, message }) => {
      if (selectedConversation?.id === conversationId) {
        setMessages(prev => [...prev, message]);
        setTimeout(scrollToBottom, 100);
      }
      fetchConversations();
    });

    socket.on('escalation', ({ conversation }) => {
      toast('New conversation in queue!', { icon: '🔔' });
      fetchConversations();
      fetchStats();
    });

    socket.on('conversation-updated', ({ conversation }) => {
      fetchConversations();
      fetchStats();
      if (selectedConversation?.id === conversation.id) {
        setSelectedConversation(conversation);
      }
    });

    return () => {
      socket.off('new-message');
      socket.off('escalation');
      socket.off('conversation-updated');
      disconnectSocket();
    };
  }, [selectedConversation?.id, fetchConversations, fetchStats]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchConversations(), fetchStats()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchConversations, fetchStats]);

  useEffect(() => {
    fetchConversations();
  }, [statusFilter, searchQuery, fetchConversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const StatusBadge = ({ status }) => {
    const colors = {
      bot: 'badge-green',
      queued: 'badge-yellow',
      assigned: 'bg-blue-100 text-blue-700',
      resolved: 'bg-neutral-300 text-neutral-600'
    };
    return <span className={`badge text-xs ${colors[status] || 'badge'}`}>{status}</span>;
  };

  const MessageBubble = ({ msg }) => {
    const isAgent = msg.sender_type === 'agent';
    const isCustomer = msg.sender_type === 'customer';
    const isBot = msg.sender_type === 'bot';

    return (
      <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'} mb-3`}>
        <div className={`max-w-[75%] rounded-lg px-4 py-2.5 ${
          isAgent
            ? 'bg-primary-500 text-white'
            : isBot
              ? 'bg-neutral-100 text-neutral-700 border border-neutral-200'
              : 'bg-white text-neutral-800 border border-neutral-200 shadow-sm'
        }`}>
          <p className={`text-xs font-medium mb-1 ${
            isAgent ? 'text-primary-100' : isBot ? 'text-neutral-400' : 'text-blue-500'
          }`}>
            {isAgent ? msg.sender_name : isBot ? 'Bot' : (msg.sender_name || 'Customer')}
          </p>
          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
          <p className={`text-xs mt-1 ${isAgent ? 'text-primary-200' : 'text-neutral-400'}`}>
            {new Date(msg.created_at).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  };

  const ConversationListItem = ({ conv, isSelected, onClick }) => {
    const timeAgo = (date) => {
      const seconds = Math.floor((new Date() - new Date(date)) / 1000);
      if (seconds < 60) return 'Just now';
      if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
      if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
      return `${Math.floor(seconds / 86400)}d ago`;
    };

    return (
      <div
        onClick={onClick}
        className={`p-4 border-b border-neutral-200 cursor-pointer hover:bg-neutral-50 transition-colors ${
          isSelected ? 'bg-primary-50 border-l-4 border-l-primary-500' : ''
        }`}
      >
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <UserCircleIcon className="w-5 h-5 text-neutral-400 flex-shrink-0" />
            <p className="text-sm font-semibold text-neutral-800 truncate">
              {conv.customer_name || conv.customer_identifier || 'Unknown'}
            </p>
          </div>
          <span className="text-xs text-neutral-400 flex-shrink-0 ml-2">
            {timeAgo(conv.last_message_at)}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs text-neutral-500 truncate flex-1">
            {conv.last_message || 'No messages yet'}
          </p>
          <StatusBadge status={conv.status} />
        </div>
        {parseInt(conv.unread_count) > 0 && (
          <div className="mt-1">
            <span className="inline-block px-1.5 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
              {conv.unread_count} new
            </span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner spinner-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="page-header">
          <h1 className="page-title">Team Inbox</h1>
          <p className="page-subtitle">Manage live conversations and take over from bots</p>
        </div>
        <div className="flex items-center gap-3">
          {stats && (
            <>
              <span className="badge badge-yellow">{stats.queued_count} Queued</span>
              <span className="badge bg-blue-100 text-blue-700">{stats.assigned_count} Assigned</span>
            </>
          )}
        </div>
      </div>

      {/* Two-panel layout */}
      <div className="card p-0 overflow-hidden" style={{ height: 'calc(100vh - 240px)' }}>
        <div className="flex h-full">
          {/* Left: Conversation list */}
          <div className="w-full md:w-96 border-r border-neutral-200 flex flex-col">
            {/* Filter tabs */}
            <div className="border-b border-neutral-200 px-4 py-3">
              <div className="flex gap-2 flex-wrap">
                {[
                  { label: 'All', value: '' },
                  { label: 'Queued', value: 'queued' },
                  { label: 'Assigned', value: 'assigned' },
                  { label: 'Bot', value: 'bot' },
                  { label: 'Resolved', value: 'resolved' },
                ].map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setStatusFilter(tab.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                      statusFilter === tab.value
                        ? 'bg-primary-500 text-white'
                        : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search */}
            <div className="px-4 py-2 border-b border-neutral-200">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
                <input
                  type="text"
                  placeholder="Search by customer..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-field text-sm py-2 pl-9"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2"
                  >
                    <XMarkIcon className="w-4 h-4 text-neutral-400 hover:text-neutral-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Conversation list (scrollable) */}
            <div className="flex-1 overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-neutral-400">
                  <ChatBubbleLeftRightIcon className="w-12 h-12 mb-3 text-neutral-300" />
                  <p className="text-sm font-medium text-neutral-500">No conversations</p>
                  <p className="text-xs mt-1">Conversations will appear here</p>
                </div>
              ) : (
                conversations.map(conv => (
                  <ConversationListItem
                    key={conv.id}
                    conv={conv}
                    isSelected={selectedConversation?.id === conv.id}
                    onClick={() => selectConversation(conv)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Right: Chat view */}
          <div className="flex-1 flex flex-col hidden md:flex">
            {selectedConversation ? (
              <>
                {/* Chat header */}
                <div className="px-6 py-4 border-b border-neutral-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-base font-semibold text-neutral-800">
                        {selectedConversation.customer_name || selectedConversation.customer_identifier || 'Unknown Customer'}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <StatusBadge status={selectedConversation.status} />
                        {selectedConversation.agent_first_name && (
                          <span className="text-xs text-neutral-500">
                            Assigned to {selectedConversation.agent_first_name}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {selectedConversation.status === 'bot' || selectedConversation.status === 'queued' ? (
                        <button onClick={handleTakeover} className="btn-primary btn-sm">
                          <HandRaisedIcon className="w-4 h-4 mr-1" />
                          Take Over
                        </button>
                      ) : null}

                      {selectedConversation.status === 'assigned' && selectedConversation.assigned_agent_id === user.id ? (
                        <button onClick={handleRelease} className="btn-outline btn-sm">
                          <ArrowPathIcon className="w-4 h-4 mr-1" />
                          Release to Bot
                        </button>
                      ) : null}

                      {selectedConversation.status !== 'resolved' ? (
                        <button onClick={handleResolve} className="btn-sm bg-green-500 text-white hover:bg-green-600">
                          <CheckCircleIcon className="w-4 h-4 mr-1" />
                          Resolve
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-6 bg-neutral-50">
                  {messages.map((msg) => (
                    <MessageBubble key={msg.id} msg={msg} />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Reply input */}
                {selectedConversation.status === 'assigned' && selectedConversation.assigned_agent_id === user.id ? (
                  <div className="px-6 py-4 border-t border-neutral-200 bg-white">
                    <form onSubmit={handleSendReply} className="flex items-end gap-3">
                      <div className="flex-1">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value)}
                          placeholder="Type your reply..."
                          rows={2}
                          className="input-field resize-none"
                          disabled={sending}
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={!replyText.trim() || sending}
                        className="btn-primary px-4 py-2.5 disabled:opacity-50"
                      >
                        <PaperAirplaneIcon className="w-5 h-5" />
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-100 text-center text-sm text-neutral-500">
                    {selectedConversation.status === 'bot' ? 'Bot is handling this conversation' :
                     selectedConversation.status === 'queued' ? 'Take over to start replying' :
                     selectedConversation.status === 'resolved' ? 'This conversation is resolved' :
                     'This conversation is assigned to another agent'}
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-neutral-400">
                <div className="text-center">
                  <InboxIcon className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
                  <p className="text-lg font-medium text-neutral-500">Select a conversation</p>
                  <p className="text-sm mt-1">Choose a conversation from the list to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamInbox;
